// Imports
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';


// Extend interface
declare global {
	interface Window {
		_paq: [string, ...unknown[]][];
	}
}


// Function component
const Matomo = () => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Initialize
	const matomoBaseURL = `https://${process.env.REACT_APP__ENVIRONMENT === 'production' ? 'matomo.hoa-express.com' : 'matomo.hoa-express-staging.com'}/`;
	
	
	// Load Matomo script
	useEffect(() => {
		if (!website?.matomoSiteID) {
			return;
		}
		
		window._paq = window._paq || [];
		
		if (window._paq.length !== 0) {
			return;
		}
		
		window._paq.push(['enableLinkTracking']);
		window._paq.push(['setTrackerUrl', `${matomoBaseURL}piwik.php`]);
		window._paq.push(['setSiteId', website?.matomoSiteID]);
		
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.async = true;
		script.defer = true;
		script.src = `${matomoBaseURL}piwik.js`;
		
		const firstScript = document.getElementsByTagName('script')[0];
		firstScript.parentNode?.insertBefore(script, firstScript);
		
		return () => {
			script.remove();
		};
	}, [website?.matomoSiteID, matomoBaseURL]);
	
	
	// Track a page view per navigation, once the title catches up (many pages set it only after their data loads)
	useEffect(() => {
		if (!website?.matomoSiteID) {
			return;
		}
		
		const previousTitle = document.title;
		const startedAt = Date.now();
		let frame: number;
		
		const trackPageView = () => {
			if (!(document.title && document.title !== previousTitle) && Date.now() - startedAt < 3000) {
				frame = requestAnimationFrame(trackPageView);
				return;
			}
			
			window._paq.push(['setCustomUrl', location.pathname]);
			window._paq.push(['setDocumentTitle', document.title]);
			window._paq.push(['trackPageView']);
		};
		
		frame = requestAnimationFrame(trackPageView);
		
		return () => {
			cancelAnimationFrame(frame);
		};
	}, [website?.matomoSiteID, location.pathname]);
	
	
	// Track links rendered after the Matomo script loads (it only listens on links that exist when tracking is enabled)
	useEffect(() => {
		if (!website?.matomoSiteID) {
			return;
		}
		
		let frame: number | undefined;
		
		const observer = new MutationObserver(() => {
			if (frame !== undefined || Array.isArray(window._paq)) {
				return;
			}
			
			frame = requestAnimationFrame(() => {
				frame = undefined;
				window._paq.push(['enableLinkTracking']);
			});
		});
		
		observer.observe(document.body, { childList: true, subtree: true });
		
		return () => {
			observer.disconnect();
			
			if (frame !== undefined) {
				cancelAnimationFrame(frame);
			}
		};
	}, [website?.matomoSiteID]);
	
	
	// Render nothing if no Matomo site ID
	if (!website?.matomoSiteID) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<noscript>
				<p>
					<img src={`${matomoBaseURL}piwik.php?idsite=${website.matomoSiteID}`} style={{ border: 0 }} alt='' />
				</p>
			</noscript>
		</React.Fragment>
	);
};

export default Matomo;
